import {
  Add as AddIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import {
  BulkAddItemsRequest,
  CreatePortfolioItemRequest,
  Portfolio,
  portfolioApi,
} from "../../services/portfolioApi";

interface BulkAddToPortfolioButtonProps {
  selectedEntities: Array<{
    id: string;
    name: string;
    type: "organization" | "facility" | "project" | "source";
  }>;
  onSuccess?: () => void;
}

const BulkAddToPortfolioButton: React.FC<BulkAddToPortfolioButtonProps> = ({
  selectedEntities,
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [newPortfolioDescription, setNewPortfolioDescription] = useState("");

  useEffect(() => {
    if (dialogOpen) {
      fetchPortfolios();
    }
  }, [dialogOpen]);

  const fetchPortfolios = async () => {
    try {
      const portfolioList = await portfolioApi.getPortfolios();
      // Filter portfolios where user has OWNER or EDITOR permissions
      const editablePortfolios = portfolioList.results.filter(
        (p) => p.user_permission === "OWNER" || p.user_permission === "EDITOR"
      );
      setPortfolios(editablePortfolios);
    } catch (error) {
      showNotification("Failed to load portfolios", "error");
    }
  };

  const handleOpen = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedPortfolio("");
    setNotes("");
    setLabels([]);
    setLabelInput("");
    setShowCreateForm(false);
    setNewPortfolioName("");
    setNewPortfolioDescription("");
  };

  const handleAddLabel = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && labelInput.trim()) {
      event.preventDefault();
      if (!labels.includes(labelInput.trim())) {
        setLabels([...labels, labelInput.trim()]);
      }
      setLabelInput("");
    }
  };

  const handleRemoveLabel = (labelToRemove: string) => {
    setLabels(labels.filter((label) => label !== labelToRemove));
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) return;

    try {
      setLoading(true);
      const newPortfolio = await portfolioApi.createPortfolio({
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim() || undefined,
      });

      // Refresh portfolios list
      await fetchPortfolios();

      // Select the newly created portfolio
      setSelectedPortfolio(newPortfolio.id);

      // Reset create form
      setNewPortfolioName("");
      setNewPortfolioDescription("");
      setShowCreateForm(false);

      showNotification(
        `Portfolio "${newPortfolio.name}" created successfully`,
        "success"
      );
    } catch (error) {
      showNotification(
        "Failed to create portfolio. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAddToPortfolio = async () => {
    if (!selectedPortfolio || selectedEntities.length === 0) return;

    try {
      setLoading(true);

      // Create bulk request with all selected entities
      const items: CreatePortfolioItemRequest[] = selectedEntities.map(
        (entity) => {
          const item: CreatePortfolioItemRequest = {
            entity_type: entity.type,
            entity_id: entity.id,
          };

          // Only include optional fields if they have values
          if (notes.trim()) {
            item.notes = notes.trim();
          }
          if (labels.length > 0) {
            item.labels = labels;
          }

          return item;
        }
      );

      const bulkRequest: BulkAddItemsRequest = { items };

      const result = await portfolioApi.bulkAddItems(
        selectedPortfolio,
        bulkRequest
      );

      const portfolioName =
        portfolios.find((p) => p.id === selectedPortfolio)?.name || "portfolio";

      if (result.created_count === result.total_requested) {
        showNotification(
          `Successfully added ${result.created_count} items to ${portfolioName}`,
          "success"
        );
      } else {
        showNotification(
          `Added ${result.created_count} of ${result.total_requested} items to ${portfolioName}. Some items may already exist.`,
          "success"
        );
      }

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to add items to portfolio. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const selectedPortfolioName =
    portfolios.find((p) => p.id === selectedPortfolio)?.name || "";

  return (
    <>
      <Button
        startIcon={<SaveIcon />}
        variant="outlined"
        size="small"
        disabled={selectedEntities.length === 0}
        onClick={handleOpen}
      >
        Save ({selectedEntities.length})
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: { backgroundColor: "#1e1e1e" },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FolderIcon />
            Add {selectedEntities.length} Items to Portfolio
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Selected entities summary */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected items ({selectedEntities.length}):
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 100,
                  overflowY: "auto",
                }}
              >
                {selectedEntities.map((entity) => (
                  <Chip
                    key={entity.id}
                    label={entity.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            {/* Portfolio selection */}
            <FormControl fullWidth>
              <InputLabel>Portfolio</InputLabel>
              <Select
                value={selectedPortfolio}
                label="Portfolio"
                onChange={(e) => setSelectedPortfolio(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                disabled={showCreateForm}
              >
                {portfolios.map((portfolio) => (
                  <MenuItem
                    key={portfolio.id}
                    value={portfolio.id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FolderOpenIcon fontSize="small" />
                      {portfolio.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Create New Portfolio Section */}
            <Box sx={{ mt: 1, mb: 2 }}>
              {!showCreateForm ? (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateForm(true)}
                  sx={{
                    textTransform: "none",
                    color: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                    },
                  }}
                >
                  Create New Portfolio
                </Button>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: "#1e1e1e",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, color: "primary.main" }}
                  >
                    Create New Portfolio
                  </Typography>

                  <TextField
                    fullWidth
                    label="Portfolio Name"
                    value={newPortfolioName}
                    onChange={(e) => setNewPortfolioName(e.target.value)}
                    margin="dense"
                    required
                    placeholder="Enter portfolio name..."
                  />

                  <TextField
                    fullWidth
                    label="Description (optional)"
                    value={newPortfolioDescription}
                    onChange={(e) => setNewPortfolioDescription(e.target.value)}
                    margin="dense"
                    multiline
                    rows={2}
                    placeholder="Brief description of this portfolio..."
                  />

                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewPortfolioName("");
                        setNewPortfolioDescription("");
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleCreatePortfolio}
                      disabled={!newPortfolioName.trim() || loading}
                    >
                      {loading ? "Creating..." : "Create"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Notes */}
            <TextField
              label="Notes (optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes for all selected items..."
              fullWidth
            />

            {/* Labels */}
            <Box>
              <TextField
                label="Labels (optional)"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={handleAddLabel}
                placeholder="Press Enter to add labels..."
                fullWidth
              />
              {labels.length > 0 && (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                >
                  {labels.map((label) => (
                    <Chip
                      key={label}
                      label={label}
                      size="small"
                      onDelete={() => handleRemoveLabel(label)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleBulkAddToPortfolio}
            variant="contained"
            disabled={
              !selectedPortfolio || selectedEntities.length === 0 || loading
            }
          >
            {loading ? "Adding..." : `Add to ${selectedPortfolioName}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkAddToPortfolioButton;
